import React, { useState, useCallback, useImperativeHandle, forwardRef, useEffect } from 'react'
import { OccasionTypeSelector } from './OccasionTypeSelector'
import { WeeklyRecurrenceSelector } from './WeeklyRecurrenceSelector'
import { MonthlyRecurrenceSelector } from './MonthlyRecurrenceSelector'
import { TimezoneSelector } from './TimezoneSelector'
import { OccasionPreview } from './OccasionPreview'

const OccasionScheduler = forwardRef(({ 
  value = {}, 
  onChange,
  template_id = null,
  className = '' 
}, ref) => {
  const [occasionConfig, setOccasionConfig] = useState({
    isOccasionDateApplicable: false,
    occasionType: 'none',
    occasionStartDate: '',
    occasionEndDate: '',
    occasionStartTime: '00:01', // Default: 12:01 AM
    occasionEndTime: '23:59',   // Default: 11:59 PM
    isRecurringYearly: false,
    weeklyRecurrenceDays: [],
    monthlyRecurrenceDays: [],
    occasionTimezone: 'Asia/Kolkata',
    occasionMetadata: {},
    ...value
  })

  const [errors, setErrors] = useState({})
  const [showPreview, setShowPreview] = useState(false)

  // Sync internal state with prop value when it changes
  useEffect(() => {
    console.log('🔄 OccasionScheduler: value prop changed:', value)
    setOccasionConfig(prev => {
      const newConfig = {
        isOccasionDateApplicable: false,
        occasionType: 'none',
        occasionStartDate: '',
        occasionEndDate: '',
        occasionStartTime: '00:01',
        occasionEndTime: '23:59',
        isRecurringYearly: false,
        weeklyRecurrenceDays: [],
        monthlyRecurrenceDays: [],
        occasionTimezone: 'Asia/Kolkata',
        occasionMetadata: {},
        ...value
      }
      console.log('📝 OccasionScheduler: updated internal config:', newConfig)
      return newConfig
    })
  }, [value])

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    getConfig: () => {
      console.log('🔍 getConfig called, returning:', occasionConfig)
      return occasionConfig
    },
    validate: () => {
      console.log('🔍 validate called')
      const validationErrors = validateConfig()
      console.log('🔍 Validation errors:', validationErrors)
      return Object.keys(validationErrors).length === 0
    }
  }), [occasionConfig])

  const updateConfig = useCallback((updates) => {
    console.log('🔄 updateConfig called with updates:', updates)
    
    setOccasionConfig(prev => {
      const newConfig = {
        ...prev,
        ...updates
      }
      console.log('📝 New occasionConfig after update:', newConfig)
      
      // Call onChange callback to notify parent component
      if (onChange) {
        console.log('📤 Calling onChange with new config:', newConfig)
        onChange(newConfig)
      }
      
      return newConfig
    })
    
    // Clear related errors
    setErrors(prev => {
      const newErrors = { ...prev }
      Object.keys(updates).forEach(key => {
        delete newErrors[key]
      })
      console.log('🧹 Cleared errors for keys:', Object.keys(updates))
      return newErrors
    })
  }, [onChange])

  const validateConfig = useCallback(() => {
    const newErrors = {}
    const { occasionType, occasionStartDate, occasionEndDate, weeklyRecurrenceDays, monthlyRecurrenceDays } = occasionConfig

    if (!occasionConfig.isOccasionDateApplicable) {
      return {} // No validation needed if occasions are disabled
    }

    switch (occasionType) {
      case 'specific_date':
        if (!occasionStartDate) {
          newErrors.occasionStartDate = 'Start date is required for specific date occasions'
        }
        break
        
      case 'date_range':
        if (!occasionStartDate) {
          newErrors.occasionStartDate = 'Start date is required for date range occasions'
        }
        if (!occasionEndDate) {
          newErrors.occasionEndDate = 'End date is required for date range occasions'
        }
        if (occasionStartDate && occasionEndDate && new Date(occasionStartDate) > new Date(occasionEndDate)) {
          newErrors.occasionEndDate = 'End date must be after start date'
        }
        break
        
      case 'recurring_yearly':
        if (!occasionStartDate) {
          newErrors.occasionStartDate = 'Start date is required for recurring yearly occasions'
        }
        break
        
      case 'weekly':
        if (!weeklyRecurrenceDays || weeklyRecurrenceDays.length === 0) {
          newErrors.weeklyRecurrenceDays = 'At least one day must be selected for weekly recurrence'
        }
        break
        
      case 'monthly':
        if (!monthlyRecurrenceDays || monthlyRecurrenceDays.length === 0) {
          newErrors.monthlyRecurrenceDays = 'At least one day must be selected for monthly recurrence'
        }
        break
    }

    setErrors(newErrors)
    return newErrors
  }, [occasionConfig])

  const handleToggleOccasions = useCallback((enabled) => {
    console.log('🔄 handleToggleOccasions called with enabled:', enabled)
    updateConfig({
      isOccasionDateApplicable: enabled,
      occasionType: enabled ? 'specific_date' : 'none'
    })
    console.log('✅ Occasion scheduling toggled to:', enabled)
  }, [updateConfig])

  const handleTypeChange = useCallback((type) => {
    console.log('🔄 handleTypeChange called with type:', type)
    
    const updates = { occasionType: type }
    
    // Reset type-specific fields when changing types
    if (type !== 'weekly') {
      updates.weeklyRecurrenceDays = []
    }
    if (type !== 'monthly') {
      updates.monthlyRecurrenceDays = []
    }
    if (!['specific_date', 'date_range', 'recurring_yearly'].includes(type)) {
      updates.occasionStartDate = ''
      updates.occasionEndDate = ''
    }
    if (type !== 'recurring_yearly') {
      updates.isRecurringYearly = false
    } else {
      updates.isRecurringYearly = true
    }
    
    console.log('🔧 Updates to apply:', updates)
    updateConfig(updates)
  }, [updateConfig])

  const handleFieldChange = useCallback((field, value) => {
    updateConfig({ [field]: value })
  }, [updateConfig])

  return (
    <div className={`occasion-scheduler bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Occasion Scheduling
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Configure when this template should be available
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            {template_id && (
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {showPreview ? 'Hide Preview' : 'Show Preview'}
              </button>
            )}
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={occasionConfig.isOccasionDateApplicable}
                onChange={(e) => handleToggleOccasions(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">
                Enable Occasion Scheduling
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Configuration Content */}
      {occasionConfig.isOccasionDateApplicable && (
        <div className="p-6 space-y-6">
          {/* Occasion Type Selection */}
          <OccasionTypeSelector
            value={occasionConfig.occasionType}
            onChange={handleTypeChange}
            error={errors.occasionType}
          />

          {/* Date Fields for date-based types */}
          {['specific_date', 'date_range', 'recurring_yearly'].includes(occasionConfig.occasionType) && (
            <div className="space-y-4">
              {/* Start Date and Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={occasionConfig.occasionStartDate}
                    onChange={(e) => handleFieldChange('occasionStartDate', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.occasionStartDate ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.occasionStartDate && (
                    <p className="text-red-600 text-sm mt-1">{errors.occasionStartDate}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={occasionConfig.occasionStartTime}
                    onChange={(e) => handleFieldChange('occasionStartTime', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Time in IST timezone</p>
                </div>
              </div>

              {/* End Date and Time (for date range and recurring yearly) */}
              {['date_range', 'recurring_yearly'].includes(occasionConfig.occasionType) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={occasionConfig.occasionEndDate}
                      onChange={(e) => handleFieldChange('occasionEndDate', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.occasionEndDate ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.occasionEndDate && (
                      <p className="text-red-600 text-sm mt-1">{errors.occasionEndDate}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Time
                    </label>
                    <input
                      type="time"
                      value={occasionConfig.occasionEndTime}
                      onChange={(e) => handleFieldChange('occasionEndTime', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Time in IST timezone</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Weekly Recurrence */}
          {occasionConfig.occasionType === 'weekly' && (
            <WeeklyRecurrenceSelector
              value={occasionConfig.weeklyRecurrenceDays}
              onChange={(days) => handleFieldChange('weeklyRecurrenceDays', days)}
              error={errors.weeklyRecurrenceDays}
            />
          )}

          {/* Monthly Recurrence */}
          {occasionConfig.occasionType === 'monthly' && (
            <MonthlyRecurrenceSelector
              value={occasionConfig.monthlyRecurrenceDays}
              onChange={(days) => handleFieldChange('monthlyRecurrenceDays', days)}
              error={errors.monthlyRecurrenceDays}
            />
          )}

          {/* Timezone Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Timezone
            </label>
            <TimezoneSelector
              value={occasionConfig.occasionTimezone}
              onChange={(timezone) => handleFieldChange('occasionTimezone', timezone)}
            />
            <p className="text-xs text-gray-500 mt-1">Default: IST (Asia/Kolkata)</p>
          </div>

          {/* Validation Status */}
          {Object.keys(errors).length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <div className="text-red-500 mr-3">⚠️</div>
                <div>
                  <h4 className="text-red-800 font-medium">Configuration Issues</h4>
                  <ul className="text-red-700 text-sm mt-1 space-y-1">
                    {Object.values(errors).map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Success Status */}
          {Object.keys(errors).length === 0 && occasionConfig.occasionType !== 'none' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="text-green-500 mr-3">✅</div>
                <div>
                  <h4 className="text-green-800 font-medium">Configuration Valid</h4>
                  <p className="text-green-700 text-sm mt-1">
                    Occasion scheduling is properly configured
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preview Panel */}
      {showPreview && template_id && occasionConfig.isOccasionDateApplicable && (
        <div className="border-t border-gray-200">
          <OccasionPreview
            template_id={template_id}
            occasionConfig={occasionConfig}
          />
        </div>
      )}
    </div>
  )
})

OccasionScheduler.displayName = 'OccasionScheduler'

export default OccasionScheduler
